import type { ReactNode } from 'react';

import { useAtomValue } from 'jotai';

import { aggregationsAtom } from '@core/Atoms/Search/AggregationsAtom';
import type { FilterKey } from '@core/Entities/Search/SearchFilters/Filter.entity';
import {
  COLORS,
  type ColorMessageId,
} from '@core/Utils/VehicleMappedMessages/ColorMappings.constants';
import { MultiSelect } from '@plp/Components/MultiSelect/MultiSelect';
import { getOptionsWithAggs } from '@plp/Utils/Filters/GetOptionsWithAggs';
import { useFilterLink } from '@search-filters/Hooks/UseFilterLink';
import {
  type MultiSelectOption,
  useMultiSelectFilters,
} from '@search-filters/Hooks/UseMultiSelectFilters';

import type { FilterControlBaseProps, PlpFiltersMessagesKeys } from '../../FilterPanel.entity';
import ApplyButton from '../ApplyButton/ApplyButton';
import { FilterLink } from '../FilterLink/FilterLink';
import { ICON_MAPPER } from './ColorFilter.constants';

import styles from './ColorFilter.module.css';

type ColorFilterProps = FilterControlBaseProps;

const filterId: FilterKey = 'color';

export const ColorFilter = ({
  strings,
  locale,
  isPlpFiltersSeoEnabled,
  tenant,
  applyButton,
}: ColorFilterProps) => {
  const { color: colorAggs } = useAtomValue(aggregationsAtom);

  const { getLinkForFilterValue } = useFilterLink({
    filterId,
    isPlpFiltersSeoEnabled,
    tenant,
  });

  const listEnhanceFn = (color: MultiSelectOption, selected: string[]) => {
    const link = getLinkForFilterValue({
      filterValue: color.id,
      resultCount: color.count,
    });
    return {
      ...color,
      renderLabel: renderLabel(
        color.labelKey as ColorMessageId,
        <FilterLink link={link}>{strings[color.labelKey as PlpFiltersMessagesKeys]}</FilterLink>,
      ),
      selected: selected?.includes(color.id),
    };
  };

  const { handleSelect, handleApply, listWithSelected } = useMultiSelectFilters(
    filterId,
    getOptionsWithAggs(COLORS, colorAggs),
    strings,
    isPlpFiltersSeoEnabled,
    tenant,
    listEnhanceFn,
    applyButton,
  );

  return (
    <section className={styles.colorFilter}>
      <MultiSelect
        groupName="color"
        classes={{ label: styles.listItem }}
        items={listWithSelected}
        onSelect={(id, checked) => {
          handleSelect(id, checked);
        }}
        dataTestId="colors-filter-list"
        applyButton={applyButton}
      />
      {applyButton && <ApplyButton handleApply={handleApply} ctaText={strings.apply} />}
    </section>
  );
};

const renderLabel = (name: ColorMessageId, label: ReactNode) => {
  const Component = ICON_MAPPER[name];
  return Component ? (
    <>
      <Component /> {label}
    </>
  ) : null;
};
